import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Polls /api/notifications every 6s for the current user.
 * For every NEW notification:
 *   1. Show a system notification (via service worker if available).
 *   2. Show an in-app toast as a fallback for when the tab is focused.
 *   3. Delete the entry from server so it isn't re-shown on reload.
 */
export function useUserNotifications(userId) {
  const seenRef = useRef(new Set());
  const swRegRef = useRef(null);

  useEffect(() => {
    if (!userId) return;
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration("/notify-sw.js").then((r) => {
        swRegRef.current = r || null;
      });
    }

    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/notifications");
        if (cancelled) return;
        const items = r.data?.items || [];
        for (const n of items) {
          const notifId = n.id;
          if (!notifId || seenRef.current.has(notifId)) continue;
          seenRef.current.add(notifId);

          const title = n.title || "New notification";
          const body = n.body || "";

          // Suppress showing stale (>10 min) notifications that queued up while offline,
          // but still delete them so they don't pile up.
          let showable = true;
          if (n.created_at) {
            const age = Date.now() - new Date(n.created_at).getTime();
            if (Number.isFinite(age) && age > 10 * 60 * 1000) showable = false;
          }

          if (showable) {
            try {
              toast.success(title, { description: body, duration: 8000 });
            } catch (_) {}
            try {
              if ("Notification" in window && Notification.permission === "granted") {
                if (swRegRef.current && swRegRef.current.active) {
                  swRegRef.current.active.postMessage({
                    type: "NOTIFY",
                    title,
                    body,
                    tag: `rodbez-notif-${notifId}`,
                    url: "/",
                  });
                } else {
                  const notif = new Notification(title, { body, tag: `rodbez-notif-${notifId}` });
                  notif.onclick = () => {
                    try { window.focus(); } catch (_) {}
                    notif.close();
                  };
                }
              }
            } catch (_) {}
            try {
              const Ctx = window.AudioContext || window.webkitAudioContext;
              if (Ctx) {
                const ctx = new Ctx();
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.frequency.value = 920;
                g.gain.setValueAtTime(0.12, ctx.currentTime);
                g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
                o.connect(g).connect(ctx.destination);
                o.start(); o.stop(ctx.currentTime + 0.3);
              }
            } catch (_) {}
          }
          // Delete from server so it doesn't replay on the next page load
          api.delete(`/notifications/${notifId}`).catch(() => {});
        }
      } catch (_) {}
    };

    tick();
    const interval = setInterval(tick, 6000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userId]);
}
